import {
  Box,
  Chip,
  Stack,
  Tooltip,
  Collapse,
  Typography,
  ListItemButton,
  CircularProgress
} from "@material-ui/core";
import {
  Check,
  Error,
  Replay,
  Warning,
  ExpandMore,
  FastForward,
  HourglassBottom
} from "@material-ui/icons";
import { useEffect, useState } from "react";
import { FilePreview } from "../../../../../@types/listings/fileData";
import { getFullName } from "utils/Haim/utils";
import { useTranslation } from "react-i18next";
import { preventBubbling } from "utils/listings/helper";
import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import { getFormattedTemplateStringFromFieldKey, getDataFromListingFieldKey } from "utils/contacts/bulkMessage/helper";
import { CUSTOM_BULK_MESSAGE_CONTACT_TEMPLATE_KEYS } from "utils/contacts/bulkMessage/new";
import { BulkMessageDialogCustomMessage } from "./BulkMessageDialogCustomMessage";
import Label from "components/Label";
import type { UsedParam } from "./BulkMessageDialogSendMessageStep";
import type { CustomBulkMessageContactTemplateKey, BulkMessageListingTemplateKey } from "utils/contacts/bulkMessage/new";
import type { Dispatch, SetStateAction } from "react";
import type { ChipProps } from "@material-ui/core";
import type {
  MessageStatus,
  MessageStatusKey,
  SelectedMessageStatus,
  BulkMessageTemplateType,
  PlaceholderTemplateForm
} from "utils/contacts/bulkMessage/new";
import type { ProcessedListingData } from "../../../../../@types/sharedSchema";
import type { BulkMessageRecipient } from "utils/contacts/bulkMessage/new";

type BulkMessageDialogContactPreviewProps = {
  index: number;
  message: string;
  listing: ProcessedListingData | null;
  contact: BulkMessageRecipient;
  filesPreview: FilePreview[];
  sendingMessage: boolean;
  bulkMessageType: BulkMessageTemplateType;
  minisiteLink?: string;
  loadingMinisiteLinks: boolean;
  selectedMessageStatus: SelectedMessageStatus;
  placeholderTemplatesForm: PlaceholderTemplateForm;
  perContactData?: Record<string, string>;
  usedParamsInMessage?: UsedParam[];
  expandAllOverride?: boolean;
  setSelectedMessageStatus: Dispatch<SetStateAction<SelectedMessageStatus>>;
  handleRetrySendingSingleBulkMessage: (contactId: string) => () => Promise<void>;
};

export function BulkMessageDialogContactPreview({
  index,
  contact,
  listing,
  message,
  filesPreview,
  sendingMessage,
  bulkMessageType,
  minisiteLink,
  loadingMinisiteLinks,
  selectedMessageStatus,
  placeholderTemplatesForm,
  perContactData,
  usedParamsInMessage,
  expandAllOverride,
  setSelectedMessageStatus,
  handleRetrySendingSingleBulkMessage
}: BulkMessageDialogContactPreviewProps): JSX.Element {
  const [open, setOpen] = useState(index === 0);

  useEffect(() => {
    if (!sendingMessage) return;

    setOpen(false);
  }, [sendingMessage]);

  useEffect(() => {
    if (expandAllOverride !== undefined) {
      setOpen(expandAllOverride);
    }
  }, [expandAllOverride]);

  const { t: translate } = useTranslation();

  const toggleOpen = (): void => setOpen(!open);

  const { id: contactId, callerFirstName, callerLastName } = contact;

  // Falls back to the derived full name for recipients with no split name fields — e.g. a
  // WhatsApp-imported audience member, who carries only the chat's display (push) name.
  const callerFullName = getFullName(callerFirstName, callerLastName) || contact.contactFullName || "";

  const messageStatus = selectedMessageStatus[contactId];

  const contactSkipped = messageStatus.status === "skipped";

  const showRetryButton = ["warning", "error"].includes(messageStatus.status) && !sendingMessage;

  const hasNoMinisite =
    !loadingMinisiteLinks && minisiteLink !== undefined && minisiteLink === "";

  const effectiveMessage = messageStatus.customMessage || message;
  const minisiteLinkUsedInMessage = effectiveMessage.includes(
    getFormattedTemplateStringFromFieldKey("minisiteLink")
  );

  const callerPhoneNumber = parsePhoneNumber(contactId).formatNational() ?? contactId;

  const primaryText = callerFullName || callerPhoneNumber;
  const secondaryText = callerFullName ? callerPhoneNumber : undefined;

  return (
    <ListItemButton
      id={getBulkMessagePreviewId(contactId)}
      disableRipple
      disableTouchRipple
      sx={{ borderRadius: 1 }}
      style={{ backgroundColor: "transparent" }}
      onClick={toggleOpen}
    >
      <Box sx={{ width: "100%" }}>
        <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
          <Stack direction="row" gap={2} alignItems="center">
            <ExpandMore
              sx={{
                transition: "transform 200ms",
                transform: open ? "rotate(180deg)" : "rotate(0deg)"
              }}
            />
            <Stack
              sx={{
                ...(contactSkipped && { opacity: 0.6 })
              }}
              gap={1}
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "start", sm: "center" }}
            >
              <Typography variant="body1" fontWeight="bold">
                {primaryText}
              </Typography>
              {secondaryText && (
                <Typography variant="body2" color="text.secondary">
                  ({secondaryText})
                </Typography>
              )}
            </Stack>
            {/* Per-contact resolved param values */}
            {usedParamsInMessage?.length > 0 && (
              <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center">
                {usedParamsInMessage.map(({ id, label, color }) => {
                  const resolvedValue = resolveParamValueForContact(
                    id, contact, listing, perContactData, placeholderTemplatesForm, minisiteLink
                  );
                  const isMissing = !resolvedValue;
                  return (
                    <Label
                      key={id}
                      variant="ghost"
                      color={isMissing ? "error" : color}
                      sx={{ fontSize: 10 }}
                    >
                      {isMissing ? `⚠️ חסר ${label} ⚠️` : resolvedValue}
                    </Label>
                  );
                })}
              </Stack>
            )}
            {hasNoMinisite && (
              <Chip
                size="small"
                color={minisiteLinkUsedInMessage ? "warning" : "info"}
                icon={minisiteLinkUsedInMessage ? <Warning /> : undefined}
                label={translate("contactsPage.bulkMessage.noMinisiteLink")}
              />
            )}
          </Stack>
          <Stack direction="row" gap={1} alignItems="center">
            {showRetryButton && (
              <Chip
                clickable
                icon={<Replay />}
                size="small"
                color="info"
                label={translate("contactsPage.control.retry")}
                onClick={preventBubbling(handleRetrySendingSingleBulkMessage(contactId))}
              />
            )}
            <BulkMessageDialogMessageStatus messageStatus={messageStatus} />
          </Stack>
        </Stack>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <BulkMessageDialogCustomMessage
            listing={listing}
            contact={contact}
            message={message}
            filesPreview={filesPreview}
            messageStatus={messageStatus}
            contactSkipped={contactSkipped}
            sendingMessage={sendingMessage}
            minisiteLink={minisiteLink}
            placeholderTemplatesForm={placeholderTemplatesForm}
            perContactData={perContactData}
            setSelectedMessageStatus={setSelectedMessageStatus!}
          />
        </Collapse>
      </Box>
    </ListItemButton>
  );
}

type BulkMessageDialogMessageStatusProps = {
  messageStatus: MessageStatus;
};

function BulkMessageDialogMessageStatus({
  messageStatus: { status, customTooltip }
}: BulkMessageDialogMessageStatusProps): JSX.Element {
  const { t: translate } = useTranslation();

  const { sx, ...rest } = messageStatusToChipProps[status];

  const label = translate(`contactsPage.bulkMessage.messageStatus.${status}`);

  let customTooltipTitle = label;

  if (customTooltip) {
    customTooltipTitle = translate(
      `contactsPage.bulkMessage.messageStatus.customTooltip.${customTooltip}`,
      {
        defaultValue: customTooltip
      }
    );
  }

  return (
    <Tooltip title={customTooltipTitle}>
      <Chip sx={{ color: "white", ...sx }} size="small" label={label} {...rest} />
    </Tooltip>
  );
}

type MessageChipProps = Record<MessageStatusKey, ChipProps>;

export const messageStatusToChipProps: MessageChipProps = {
  pending: {
    sx: { color: "initial" },
    color: "default",
    icon: <HourglassBottom />
  },
  loading: {
    color: "info",
    icon: <CircularProgress size={16} />
  },
  success: {
    color: "success",
    icon: <Check />
  },
  error: {
    color: "error",
    icon: <Error />
  },
  warning: {
    color: "warning",
    icon: <Warning />
  },
  skipped: {
    color: "primary",
    icon: <FastForward />
  }
};

export function getBulkMessagePreviewId(contactId: string): string {
  return `bulk-message-preview-${contactId}`;
}

function resolveParamValueForContact(
  paramId: string,
  contact: BulkMessageRecipient,
  listing: ProcessedListingData | null,
  perContactData?: Record<string, string>,
  placeholders?: PlaceholderTemplateForm,
  minisiteLink?: string
): string {
  // 1. Check per-contact custom data first
  if (perContactData?.[paramId]) return perContactData[paramId];

  // 2. Check custom contact keys (minisiteLink etc.)
  if (CUSTOM_BULK_MESSAGE_CONTACT_TEMPLATE_KEYS.includes(paramId as CustomBulkMessageContactTemplateKey)) {
    if (paramId === "minisiteLink" && minisiteLink) return minisiteLink;
    return "";
  }

  // 3. Check contact fields
  const contactValue = contact?.[paramId];
  if (contactValue) return String(contactValue);

  // 4. Check placeholder fallbacks
  if (placeholders?.[paramId]) return placeholders[paramId];

  // 5. Check listing fields
  if (listing) {
    const listingValue = getDataFromListingFieldKey(paramId as BulkMessageListingTemplateKey, listing);
    if (listingValue) return listingValue;
  }

  return "";
}
